import i18nFactory from '@yandex-int/i18n';
import { Action } from 'redux';
import { Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { NoticeType, showNoticeMessage } from '../../actions/grid-screen';
import {
    addProduct,
    changeProductsForm,
    FETCH_ADD_INTERNAL_PRODUCT,
    FETCH_CHANGE_INTERNAL_PRODUCT,
    FETCH_INTERNAL_PRODUCTS_DATA,
    FetchAddProductAction,
    FetchChangeProductAction,
    setProductsPageStatus,
    updateProduct,
    updateProductsData
} from '../../actions/products';
import { selectProductsSortParams } from '../../selectors/products';
import { EpicDeps } from '../../service/StoreFactory';
import { ProductsPageStates } from '../../typings/products';
import { State } from '../../typings/state';

import * as keyset from './products.i18n';
const i18n = i18nFactory(keyset);

export const fetchProductsDataEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) =>
    action$.pipe(
        ofType(FETCH_INTERNAL_PRODUCTS_DATA),
        mergeMap(
            (): Observable<Action> => {
                return from(deps.productsDataProvider.fetchProductsData()).pipe(
                    mergeMap(response => {
                        const sortParams = selectProductsSortParams(
                            store$.value
                        );

                        return of(
                            updateProductsData(response, sortParams),
                            setProductsPageStatus(ProductsPageStates.LOADED)
                        );
                    }),
                    catchError(() =>
                        of(setProductsPageStatus(ProductsPageStates.ERROR))
                    )
                );
            }
        )
    );

export const addProductsDataEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) =>
    action$.pipe(
        ofType(FETCH_ADD_INTERNAL_PRODUCT),
        mergeMap(
            (action: FetchAddProductAction): Observable<Action> => {
                return from(
                    deps.productsDataProvider.addNewProduct(action.product)
                ).pipe(
                    mergeMap(response => {
                        const sortParams = selectProductsSortParams(
                            store$.value
                        );

                        showNoticeMessage(
                            NoticeType.success,
                            i18n('success-add')
                        );

                        return of(
                            addProduct(response, sortParams),
                            setProductsPageStatus(ProductsPageStates.LOADED),
                            changeProductsForm({
                                product: {
                                    productName: '',
                                    description: ''
                                },
                                isValid: true,
                                isEnabled: false
                            })
                        );
                    }),
                    catchError(() => {
                        showNoticeMessage(NoticeType.error, i18n('error-add'));

                        return of(
                            setProductsPageStatus(ProductsPageStates.PENDING)
                        );
                    })
                );
            }
        )
    );

export const changeProductDataEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) =>
    action$.pipe(
        ofType(FETCH_CHANGE_INTERNAL_PRODUCT),
        mergeMap(
            (action: FetchChangeProductAction): Observable<Action> => {
                return from(
                    deps.productsDataProvider.changeProduct(action.product)
                ).pipe(
                    mergeMap(response => {
                        showNoticeMessage(
                            NoticeType.success,
                            i18n('success-change')
                        );

                        return of(
                            updateProduct(response),
                            setProductsPageStatus(ProductsPageStates.LOADED)
                        );
                    }),
                    catchError(() => {
                        showNoticeMessage(
                            NoticeType.error,
                            i18n('error-change')
                        );

                        return of(
                            setProductsPageStatus(ProductsPageStates.PENDING)
                        );
                    })
                );
            }
        )
    );
